import { v4 as uuid } from 'uuid';
import { citations, reactions } from '@Constants';
import Catalyst from '@Services/Catalyst';
import AppStorage from '@Services/AppStorage';

const ASSISTANT_ANALYTICS_CONTEXT = {
  [citations]: 'documents',
  [reactions]: 'reactions',
};

export const ASSISTANT_FEATURES = Object.freeze({
  COLLAPSE_SIDE_PANE: 'collapse:side-pane-button',
  ASSISTANT_SIDE_PANE: 'ai-assistant:side-pane-button',
  FILTERS_SIDE_PANE: 'filters:side-pane-button',
  SUMMARIZE_RESULTS: 'ai-assistant:summarize-results',
  SUMMARIZE_SELECTED: 'ai-assistant:summarize-selected-results',
  STOP_GENERATING: 'ai-assistant:stop-generating',
  REFERENCE_CLICK: 'ai-summary:reference-click',
  VIEW_REFERENCED_ONLY: 'ai-summary:view-referenced-only',
  LEARN_HOW_IT_WORKS: 'ai-summary:learn-how-it-works',
  COPY_INSIGHTS: 'ai-summary:copy-insights',
  REGENERATE_SUMMARY: 'ai-summary:regenerate-summary',
});

export function getAssistantAnalyticsContext(context) {
  return ASSISTANT_ANALYTICS_CONTEXT[context];
}

export function trackAssistantFeatureClick(action, context) {
  const analyticsContext = getAssistantAnalyticsContext(context);
  if (!action || !analyticsContext) return;

  Catalyst.trackEvent('buttonClick', {
    buttonType: `rx2:${action}:${analyticsContext}`,
  });
}

function countWords(text = '') {
  // Remove extra whitespace, split by spaces, filter out empty strings
  return text.trim().split(/\s+/).filter(Boolean).length;
}

export function trackGenAIContentUpdated({ summaryResponse = {}, activeBreadcrumb = {}, context, processingTime }) {
  const { summary, referencedCitations, referencedReactions } = summaryResponse;
  const { isHistoryBreadcrumb, quickSearchItem } = activeBreadcrumb;
  const resultContext = context || summaryResponse?.context || activeBreadcrumb?.context;
  if (!getAssistantAnalyticsContext(resultContext)) return;

  const quickSearchExampleUsed = AppStorage.instance.getFromSessionStorage('quickSearchExampleUsed');
  const quickSearchContent = quickSearchItem?.content;
  const quickSearchStructure = quickSearchItem?.structure;

  const inputSource = quickSearchExampleUsed && quickSearchExampleUsed === quickSearchItem?.content ? 'example link' : 'user';
  const inputOrigin = isHistoryBreadcrumb ? 'history' : 'first search';
  // FR-4.4: select by context because reaction responses can also include an empty
  // `referencedCitations` array.
  const citationsCount = resultContext === reactions ? (referencedReactions?.length ?? 0) : (referencedCitations?.length ?? 0);
  const resultWordCount = countWords(summary);
  const conversationId = uuid();
  const promptCounter = '1';

  let answerDetails = 'tools=[]';
  if (quickSearchContent && quickSearchStructure) {
    answerDetails = 'tools=[text + structure search]';
  } else if (quickSearchContent) {
    answerDetails = 'tools=[text search]';
  } else if (quickSearchStructure) {
    answerDetails = 'tools=[structure search]';
  }

  Catalyst.trackEvent('genAIContentUpdated', {
    genAI: {
      inputSource, // GenAI Details [v58], who formulated the input? "user" = user entered his own question, "example link" - user chose one of the questions proposed by the tool
      inputOrigin, // GenAI Details [v58],  where was the input triggered from - first question, follow-up question, etc.
      citationsCount, // GenAI Citations [e342], number of citations returned
      resultWordCount, // GenAI Result Word Count [e340], number of words returned
      answerDetails, // GenAI Answer Details [p45], tools = shows all tools used to generate the answer (e.g. tools to analyse the drawn structures or written text)
      processingTime: processingTime === undefined ? undefined : String(processingTime), // GenAI Processing Time [e362], submitted as string in ms
      conversationId, // GenAI ConversationID and PromptCounter [p46], GenAI Unique Conversations [e364], unique conversation id (unique across all users)
      promptCounter, // GenAI ConversationID and PromptCounter [p46], GenAI Unique Prompts [e365], it can be a simple counter as combined with conversation-id is unique
    },
    content: [], // Anything else you may find relevant to send about the content involved in the AI-generated summary
  });
}

export function trackSummaryGenerationError() {
  Catalyst.trackEvent('formError', {
    form: {
      errorType: 'fe-rx2:results:citations:ai:summary generation failed', // Error Type [v43]
    },
  });
}

export function trackSurveySubmissionForSummary(liked, context) {
  const analyticsContext = getAssistantAnalyticsContext(context);
  if (!analyticsContext) return;

  const isReaction = analyticsContext === 'reactions';
  Catalyst.trackEvent('SurveySubmission', {
    survey: {
      question: 'Was this summary helpful?', // Survey Meta Data [p43, v56]
      score: liked ? '2' : '1', // Survey Score [e322] - 1 for Thumb Down, 2 for Thumb Up
      targeting: `Reaxys AI - ${isReaction ? 'Reaction' : 'Document'} Summary Evaluation`, // Survey Meta Data [p43, v56]
      type: `GenAI-Summary-BinaryScore-${isReaction ? 'Reactions' : 'Documents'}`, // Survey Meta Data [p43, v56]
    },
  });
}

export function trackHowItWorksClick(context) {
  trackAssistantFeatureClick(ASSISTANT_FEATURES.LEARN_HOW_IT_WORKS, context);
}

export function trackCopyToClipboard(context) {
  trackAssistantFeatureClick(ASSISTANT_FEATURES.COPY_INSIGHTS, context);
}

// FR-4.4/FR-1.9: a user cancel is distinct from a genuine generation failure —
// `trackSummaryGenerationError` stays reserved for timeouts/errors only.
export function trackSummaryGenerationCancelled(context) {
  trackAssistantFeatureClick(ASSISTANT_FEATURES.STOP_GENERATING, context);
}
